import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { api, idempotencyKey, tokenStore, unwrap } from "./client";
import { qk } from "./keys";
import { ApiError } from "./problem";
import { useActiveOrgId } from "@/auth/session";
import type {
  ApiKeyCreated,
  CreateRefillBody,
  Device,
  DeviceCommand,
  DeviceLogLine,
  FirmwareImage,
  Generator,
  Invitation,
  InvitationPreview,
  Member,
  Organization,
  OrganizationRole,
  Refill,
  RefillRequest,
  Region,
  ReportExport,
  ReportFormat,
  ReportSummary,
  ReportType,
  Session,
  Site,
  Tank,
  UpdateRefillBody,
} from "./types";

/* ------------------------------------------------------------------ auth */
/* Identity is global — none of these are tenant-scoped, and none of them may be. */

export function useRegister() {
  return useMutation({
    mutationFn: (body: {
      email: string;
      password: string;
      name: string;
      invitation_token?: string;
    }) => unwrap(api.POST("/v1/auth/register", { body })),
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { email: string; password: string }): Promise<Session> => {
      const session = await unwrap(api.POST("/v1/auth/login", { body }));
      if (session.access_token) tokenStore.set(session.access_token, session.expires_at);
      return session;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.me() });
    },
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (body: { email: string }) =>
      unwrap(api.POST("/v1/auth/forgot-password", { body })),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (body: { token: string; password: string }) =>
      unwrap(api.POST("/v1/auth/reset-password", { body })),
  });
}

export function useVerifyEmail() {
  return useMutation({
    mutationFn: (body: { token: string }) => unwrap(api.POST("/v1/auth/verify-email", { body })),
  });
}

export function useResendVerification() {
  return useMutation({
    mutationFn: (body: { email: string }) =>
      unwrap(api.POST("/v1/auth/resend-verification", { body })),
  });
}

/** The spec models Google sign-in as a browser redirect, not an XHR — so we navigate. */
export function googleSignInUrl(redirectTo?: string): string {
  const base = `${import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8080"}/v1/auth/google`;
  return redirectTo ? `${base}?redirect_uri=${encodeURIComponent(redirectTo)}` : base;
}

/* ---------------------------------------------------------- organizations */

export function useMyOrganizations() {
  return useQuery({
    queryKey: qk.myOrganizations(),
    queryFn: () => unwrap(api.GET("/v1/organizations", {})),
  });
}

export function useCreateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { name: string; slug?: string }): Promise<Organization> => {
      const org = await unwrap(api.POST("/v1/organizations", { body }));

      // CREATING AN ORGANIZATION DOES NOT PUT YOU IN IT. YOU HAVE TO WALK THROUGH THE DOOR.
      //
      // This used to carry a comment saying "creating an org switches the session into it".
      // That was simply false — the server does no such thing. CreateOrganization creates a
      // tenant; binding the SESSION to a tenant is a separate, explicit /switch, because a
      // session carries WHO you are and WHICH ORG YOU ARE ACTING IN as two different facts.
      //
      // The cost of that false comment: a customer typed their company name, hit "Create
      // organization", the server said 201 — and the app bounced them straight back to the
      // create-organization form, because the guard beyond it still saw no active org. They
      // could create a second, and a third, and never once get in. Forever. Nothing in the
      // 95 component tests or the 1290 Go tests could see it, because it only exists in the
      // seam between them.
      //
      // So: walk through the door.
      await unwrap(
        api.POST("/v1/organizations/{orgId}/switch", {
          params: { path: { orgId: org.id } },
        }),
      );

      // Then WAIT for /v1/me to actually reflect the new active org before this mutation is
      // allowed to report success. refetchQueries awaits the network round-trip, so by the
      // time the component's onSuccess navigates to the next onboarding step, the session
      // genuinely holds the org — and the guard on that step admits it instead of bouncing
      // the customer straight back to the create-organization form. Invalidate-and-hope
      // raced that navigation; this does not race it.
      await queryClient.refetchQueries({ queryKey: qk.me() });
      return org;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.myOrganizations() });
    },
  });
}

export function useOrganization() {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: qk.organization(orgId),
    queryFn: () => unwrap(api.GET("/v1/organizations/{orgId}", { params: { path: { orgId } } })),
  });
}

export function useUpdateOrganization() {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name?: string;
      settings?: {
        timezone?: string;
        low_fuel_threshold_percent?: number;
        refill_overdue_days?: number;
        contact_email?: string;
      };
    }): Promise<Organization> =>
      unwrap(api.PATCH("/v1/organizations/{orgId}", { params: { path: { orgId } }, body })),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.organization(orgId) });
      await queryClient.invalidateQueries({ queryKey: qk.me() });
    },
  });
}

/* ------------------------------------------------------------------ sites */

// Type aliases, not interfaces: an interface has no implicit index signature, so it cannot
// be passed to the `Filters` record the cache-key factory takes. This is the one place that
// distinction matters, and it matters a lot — it is what keys every tenant-scoped query.
export type SiteFilters = {
  q?: string;
  region_id?: string;
  status?: "active" | "inactive";
  limit?: number;
};

export function useSites(filters: SiteFilters = {}) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: qk.sites(orgId, filters),
    queryFn: () => unwrap(api.GET("/v1/sites", { params: { query: filters } })),
  });
}

export function useSite(siteId: string) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: qk.site(orgId, siteId),
    queryFn: () => unwrap(api.GET("/v1/sites/{siteId}", { params: { path: { siteId } } })),
  });
}

export function useCreateSite() {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      code?: string;
      region_id?: string;
      address?: string;
    }): Promise<Site> => unwrap(api.POST("/v1/sites", { body })),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [...qk.org(orgId), "sites"] });
    },
  });
}

export function useUpdateSite(siteId: string) {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name?: string;
      code?: string;
      region_id?: string;
      address?: string;
      status?: "active" | "inactive";
    }): Promise<Site> =>
      unwrap(api.PATCH("/v1/sites/{siteId}", { params: { path: { siteId } }, body })),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.site(orgId, siteId) });
      await queryClient.invalidateQueries({ queryKey: [...qk.org(orgId), "sites"] });
    },
  });
}

export function useDeleteSite() {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (siteId: string) =>
      unwrap(api.DELETE("/v1/sites/{siteId}", { params: { path: { siteId } } })),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [...qk.org(orgId), "sites"] });
    },
  });
}

/* ---------------------------------------------------- tanks & generators */

/**
 * Decommission a tank.
 *
 * The endpoint is DELETE, but the SERVER DOES NOT DESTROY ANYTHING that has history: a
 * tank with recorded refills answers 409 — *"this tank has recorded refills — decommission
 * it instead, so its fuel history survives"* — because deleting it would erase the
 * customer's audit trail, which is the entire thing they bought this product for.
 *
 * So the UI must not offer a "Delete" that it knows will 409. Offer what actually happens.
 * A tank with no history deletes cleanly (204); a tank with history is decommissioned via
 * PATCH status. Surface the 409 as the sentence the server sent, not as "something went
 * wrong" — the server's wording already tells the customer what to do instead.
 */
export function useDeleteTank(siteId: string) {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tankId: string) =>
      unwrap(api.DELETE("/v1/tanks/{tankId}", { params: { path: { tankId } } })),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.siteTanks(orgId, siteId) });
      await queryClient.invalidateQueries({ queryKey: qk.site(orgId, siteId) });
    },
  });
}

/**
 * Decommission a generator.
 *
 * A generator's hour meter is the denominator of every consumption figure the product has
 * ever produced. Destroying the row would not remove a machine from a yard — it would make
 * "we burned X litres in Y hours" retroactively unanswerable. It goes out of service; its
 * numbers stay on the books.
 */
export function useDeleteGenerator(siteId: string) {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (generatorId: string) =>
      unwrap(
        api.DELETE("/v1/generators/{generatorId}", {
          params: { path: { generatorId } },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.siteGenerators(orgId, siteId) });
      await queryClient.invalidateQueries({ queryKey: qk.site(orgId, siteId) });
    },
  });
}

export function useSiteTanks(siteId: string, options?: { enabled?: boolean }) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: qk.siteTanks(orgId, siteId),
    queryFn: () =>
      unwrap(api.GET("/v1/sites/{siteId}/tanks", { params: { path: { siteId } } })),
    enabled: options?.enabled ?? true,
  });
}

export function useSiteGenerators(siteId: string, options?: { enabled?: boolean }) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: qk.siteGenerators(orgId, siteId),
    queryFn: () =>
      unwrap(api.GET("/v1/sites/{siteId}/generators", { params: { path: { siteId } } })),
    enabled: options?.enabled ?? true,
  });
}

export function useCreateTank(siteId: string) {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      capacity_liters: number;
      fuel_type?: "diesel" | "petrol" | "kerosene";
      low_level_threshold_percent?: number;
      initial_level_liters?: number;
    }): Promise<Tank> =>
      unwrap(api.POST("/v1/sites/{siteId}/tanks", { params: { path: { siteId } }, body })),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.siteTanks(orgId, siteId) });
      await queryClient.invalidateQueries({ queryKey: qk.site(orgId, siteId) });
    },
  });
}

export function useCreateGenerator(siteId: string) {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      kva_rating: number;
      manufacturer?: string;
      model?: string;
      serial_number?: string;
      running_hours?: number;
    }): Promise<Generator> =>
      unwrap(
        api.POST("/v1/sites/{siteId}/generators", {
          params: { path: { siteId } },
          // The spec gives `running_hours` a default of 0, which makes it required in the
          // generated request type. A brand-new generator has run for zero hours.
          body: { ...body, running_hours: body.running_hours ?? 0 },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.siteGenerators(orgId, siteId) });
      await queryClient.invalidateQueries({ queryKey: qk.site(orgId, siteId) });
    },
  });
}

/**
 * Editing and retiring a tank.
 *
 * There is no `useDeleteTank` here on purpose. The spec's `deleteTank` exists, but it 409s
 * for any tank that has ever been refilled — which is every tank a customer cares about —
 * and the fix it names is `decommission it instead`. A hook whose only honest use is on a
 * tank nobody has used yet is a trap, so the UI offers the thing that always works and
 * always keeps the history: `status: "decommissioned"`.
 */
export function useUpdateTank(siteId: string) {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tankId,
      ...body
    }: {
      tankId: string;
      name?: string;
      capacity_liters?: number;
      fuel_type?: "diesel" | "petrol" | "kerosene";
      low_level_threshold_percent?: number;
      status?: "active" | "decommissioned";
    }): Promise<Tank> =>
      unwrap(api.PATCH("/v1/tanks/{tankId}", { params: { path: { tankId } }, body })),
    onSuccess: async () => {
      // A tank's capacity and threshold decide whether it reads as low, and the dashboard
      // reads the same rows — so the whole org's derived views are now suspect.
      await queryClient.invalidateQueries({ queryKey: qk.siteTanks(orgId, siteId) });
      await queryClient.invalidateQueries({ queryKey: qk.site(orgId, siteId) });
    },
  });
}

export function useUpdateGenerator(siteId: string) {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      generatorId,
      ...body
    }: {
      generatorId: string;
      name?: string;
      kva_rating?: number;
      manufacturer?: string;
      model?: string;
      serial_number?: string;
      // Monotonic. The server 422s a value below the current reading — see the spec.
      running_hours?: number;
      status?: "active" | "maintenance" | "decommissioned";
    }): Promise<Generator> =>
      unwrap(
        api.PATCH("/v1/generators/{generatorId}", {
          params: { path: { generatorId } },
          body,
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.siteGenerators(orgId, siteId) });
      await queryClient.invalidateQueries({ queryKey: qk.site(orgId, siteId) });
    },
  });
}

/* ------------------------------------------------------------ live telemetry
 *
 * The monitoring page reads two different shapes of the same truth:
 *
 *   - `useTank` / `useGenerator` — the CURRENT reading. Polled on an interval so a tile
 *     updates itself without the operator refreshing the page. This is the same
 *     `GET /v1/tanks/{tankId}` the rest of the app uses, not a bespoke "live" endpoint —
 *     there is only one truth about a tank's level, and this page just asks for it more
 *     often.
 *   - `useTankReadings` / `useGeneratorReadings` — the TIME SERIES behind the trend chart.
 *     Not polled by default: a 24-hour window does not need to be re-fetched every few
 *     seconds, and the caller can pass its own `refetchInterval` if a screen wants that.
 */

/** How often a live tile re-asks the server. Fast enough to feel alive, not a hammer. */
const LIVE_POLL_MS = 7_000;

export function useTank(tankId: string, options?: { enabled?: boolean }) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: qk.tank(orgId, tankId),
    queryFn: () => unwrap(api.GET("/v1/tanks/{tankId}", { params: { path: { tankId } } })),
    enabled: options?.enabled ?? true,
    refetchInterval: LIVE_POLL_MS,
    // A stale tile reading as fresh is worse than an extra request — the whole point of
    // this hook is "trust what's on screen right now".
    staleTime: 0,
  });
}

export function useGenerator(generatorId: string, options?: { enabled?: boolean }) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: qk.generator(orgId, generatorId),
    queryFn: () =>
      unwrap(api.GET("/v1/generators/{generatorId}", { params: { path: { generatorId } } })),
    enabled: options?.enabled ?? true,
    refetchInterval: LIVE_POLL_MS,
    staleTime: 0,
  });
}

export type ReadingWindow = { from?: string; to?: string; limit?: number };

export function useTankReadings(
  tankId: string,
  window_: ReadingWindow = {},
  options?: { enabled?: boolean },
) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: qk.tankReadings(orgId, tankId, window_),
    queryFn: () =>
      unwrap(
        api.GET("/v1/tanks/{tankId}/readings", {
          params: { path: { tankId }, query: window_ },
        }),
      ),
    enabled: options?.enabled ?? true,
  });
}

export function useGeneratorReadings(
  generatorId: string,
  window_: ReadingWindow = {},
  options?: { enabled?: boolean },
) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: qk.generatorReadings(orgId, generatorId, window_),
    queryFn: () =>
      unwrap(
        api.GET("/v1/generators/{generatorId}/readings", {
          params: { path: { generatorId }, query: window_ },
        }),
      ),
    enabled: options?.enabled ?? true,
  });
}

/* ----------------------------------------------------------- field boards
 *
 * The ESP32 generator monitors. A board registers itself on its first report, so there is
 * no "create" here — only watching it, naming it, pointing it at a generator, and talking to
 * it through the console.
 *
 * Poll intervals are set by what each surface is FOR: the list and the live tiles every 5 s
 * (a board syncs every ~3 s and `online` is a 30 s window), the hour-long trend every 10 s,
 * the log tail every 3 s — the same cadence the board itself syncs at, so a line shows up
 * about as soon as it could.
 */

const DEVICE_POLL_MS = 5_000;
const DEVICE_TREND_POLL_MS = 10_000;
const DEVICE_LOG_POLL_MS = 3_000;
const DEVICE_COMMAND_POLL_MS = 1_000;

/** The log tail keeps at most this many lines in memory — enough to scroll back through a
 * boot, not so many that a board left chatting overnight eats the tab. */
export const DEVICE_LOG_LINE_CAP = 2_000;

export function useDevices() {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: qk.devices(orgId),
    queryFn: () => unwrap(api.GET("/v1/devices", { params: { query: { limit: 100 } } })),
    refetchInterval: DEVICE_POLL_MS,
    staleTime: 0,
  });
}

export function useDevice(deviceId: string) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: qk.device(orgId, deviceId),
    queryFn: () => unwrap(api.GET("/v1/devices/{deviceId}", { params: { path: { deviceId } } })),
    refetchInterval: DEVICE_POLL_MS,
    staleTime: 0,
  });
}

/**
 * The board's readings over the last `minutes`.
 *
 * The window is computed INSIDE the query function, not passed in as `from`: a `from` built
 * during render changes every render, which would mint a new cache key each time and refetch
 * forever. The key names the window's length; each poll slides it to end at "now".
 */
export function useDeviceReadings(deviceId: string, minutes = 60) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: qk.deviceReadings(orgId, deviceId, { minutes }),
    queryFn: () =>
      unwrap(
        api.GET("/v1/devices/{deviceId}/readings", {
          params: {
            path: { deviceId },
            // A board reports every few seconds — an hour is ~1,200 points, over the default
            // page. Ask for enough that the chart spans the whole window.
            query: {
              from: new Date(Date.now() - minutes * 60_000).toISOString(),
              limit: 2_000,
            },
          },
        }),
      ),
    refetchInterval: DEVICE_TREND_POLL_MS,
  });
}

export interface DeviceLogTail {
  lines: DeviceLogLine[];
  nextAfter: number;
}

/**
 * Follow a board's log stream.
 *
 * The cache entry IS the tail: each poll reads the previous `next_after` out of the entry it
 * is about to replace, asks only for lines after it, and appends. So the tail survives
 * navigating away and back, never re-downloads what it already holds, and the cap is applied
 * in exactly one place. `paused` stops the polling; resuming catches up from where it left
 * off, because the server keeps 14 days of lines.
 */
export function useDeviceLogTail(
  deviceId: string,
  options: { enabled: boolean; paused: boolean },
) {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  const queryKey = qk.deviceLogs(orgId, deviceId);
  return useQuery({
    queryKey,
    queryFn: async (): Promise<DeviceLogTail> => {
      const previous = queryClient.getQueryData<DeviceLogTail>(queryKey);
      const page = await unwrap(
        api.GET("/v1/devices/{deviceId}/logs", {
          params: {
            path: { deviceId },
            query: previous ? { after: previous.nextAfter, limit: 1000 } : { limit: 200 },
          },
        }),
      );
      const lines = [...(previous?.lines ?? []), ...page.data].slice(-DEVICE_LOG_LINE_CAP);
      return { lines, nextAfter: page.next_after };
    },
    enabled: options.enabled,
    refetchInterval: options.paused ? false : DEVICE_LOG_POLL_MS,
    staleTime: 0,
  });
}

export function useDeviceCommands(deviceId: string, options: { enabled: boolean }) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: qk.deviceCommands(orgId, deviceId),
    queryFn: () =>
      unwrap(
        api.GET("/v1/devices/{deviceId}/commands", {
          params: { path: { deviceId }, query: { limit: 20 } },
        }),
      ),
    enabled: options.enabled,
  });
}

/** One command, polled every second until the board has returned its output. */
export function useDeviceCommand(deviceId: string, commandId: string | undefined) {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: qk.deviceCommand(orgId, deviceId, commandId ?? ""),
    queryFn: async (): Promise<DeviceCommand> => {
      const command = await unwrap(
        api.GET("/v1/devices/{deviceId}/commands/{commandId}", {
          params: { path: { deviceId, commandId: commandId ?? "" } },
        }),
      );
      if (command.status === "done") {
        await queryClient.invalidateQueries({ queryKey: qk.deviceCommands(orgId, deviceId) });
      }
      return command;
    },
    enabled: !!commandId,
    refetchInterval: (query) =>
      query.state.data?.status === "done" ? false : DEVICE_COMMAND_POLL_MS,
  });
}

export function useCreateDeviceCommand(deviceId: string) {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (command: string): Promise<DeviceCommand> =>
      unwrap(
        api.POST("/v1/devices/{deviceId}/commands", {
          params: { path: { deviceId } },
          body: { command },
        }),
      ),
    onSuccess: async (command) => {
      queryClient.setQueryData(qk.deviceCommand(orgId, deviceId, command.id), command);
      await queryClient.invalidateQueries({ queryKey: qk.deviceCommands(orgId, deviceId) });
    },
  });
}

export function useUpdateDevice(deviceId: string) {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string }): Promise<Device> =>
      unwrap(api.PATCH("/v1/devices/{deviceId}", { params: { path: { deviceId } }, body })),
    onSuccess: async (device) => {
      queryClient.setQueryData(qk.device(orgId, deviceId), device);
      await queryClient.invalidateQueries({ queryKey: qk.devices(orgId) });
    },
  });
}

/** `generatorId: null` unlinks. One mutation, because to the operator it is one control. */
export function useSetDeviceGenerator(deviceId: string) {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (generatorId: string | null): Promise<void> => {
      if (generatorId === null) {
        await unwrap(
          api.DELETE("/v1/devices/{deviceId}/generator", { params: { path: { deviceId } } }),
        );
        return;
      }
      await unwrap(
        api.PUT("/v1/devices/{deviceId}/generator", {
          params: { path: { deviceId } },
          body: { generator_id: generatorId },
        }),
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.device(orgId, deviceId) });
      await queryClient.invalidateQueries({ queryKey: qk.devices(orgId) });
    },
  });
}

/** Every generator in the org, across its active sites — for the "which generator" picker.
 * Same per-site cache keys the sites and monitoring screens use, so nothing is fetched twice. */
export function useOrgGenerators(options: { enabled: boolean }) {
  const orgId = useActiveOrgId();
  const sitesQuery = useSites({ status: "active", limit: 100 });
  const sites = sitesQuery.data?.data ?? [];
  return useQueries({
    queries: sites.map((site) => ({
      queryKey: qk.siteGenerators(orgId, site.id),
      queryFn: () =>
        unwrap(
          api.GET("/v1/sites/{siteId}/generators", { params: { path: { siteId: site.id } } }),
        ),
      enabled: options.enabled,
    })),
    combine: (results) => ({
      generators: results.flatMap((r, i) =>
        (r.data?.data ?? []).map((generator) => ({
          generator,
          siteName: sites[i]?.name ?? "",
        })),
      ),
      isPending: sitesQuery.isPending || results.some((r) => r.isPending),
    }),
  });
}

/**
 * The org's current firmware image. The spec answers 404 when none has been uploaded — that
 * is a state of the organization, not an error, so it comes back as `null`.
 */
export function useFirmware(options: { enabled: boolean }) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: qk.firmware(orgId),
    queryFn: async (): Promise<FirmwareImage | null> => {
      try {
        return await unwrap(api.GET("/v1/firmware", {}));
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
    enabled: options.enabled,
  });
}

export function useUploadFirmware() {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: Blob): Promise<FirmwareImage> =>
      unwrap(
        api.POST("/v1/firmware", {
          // The spec's `format: binary` body generates as `string`, but the wire body is the
          // raw image: hand the Blob to fetch untouched rather than JSON-encoding it.
          body: file as unknown as string,
          bodySerializer: (body) => body,
          headers: { "Content-Type": "application/octet-stream" },
        }),
      ),
    onSuccess: (image) => {
      queryClient.setQueryData(qk.firmware(orgId), image);
    },
  });
}

/* ---------------------------------------------------------------- regions */

export function useRegions() {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: qk.regions(orgId),
    queryFn: () => unwrap(api.GET("/v1/regions", {})),
  });
}

export function useCreateRegion() {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; code?: string; description?: string }): Promise<Region> =>
      unwrap(api.POST("/v1/regions", { body })),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.regions(orgId) });
    },
  });
}

/* ---------------------------------------------------------------- refills */

export type RefillFilters = {
  site_id?: string;
  tank_id?: string;
  status?: "pending" | "completed" | "cancelled";
  recorded_from?: string;
  recorded_to?: string;
  limit?: number;
};

export function useRefills(filters: RefillFilters = {}) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: qk.refills(orgId, filters),
    queryFn: () => unwrap(api.GET("/v1/refills", { params: { query: filters } })),
  });
}

export function useRefill(refillId: string) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: qk.refill(orgId, refillId),
    queryFn: () => unwrap(api.GET("/v1/refills/{refillId}", { params: { path: { refillId } } })),
  });
}

export function useCreateRefill() {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRefillBody): Promise<Refill> =>
      unwrap(
        api.POST("/v1/refills", {
          body,
          // A refill is fuel-shaped: a double-tap on a phone in a generator room must not
          // create two of them. The spec gives us Idempotency-Key for exactly this.
          params: { header: { "Idempotency-Key": idempotencyKey() } },
        }),
      ),
    onSuccess: async () => {
      // A refill moves a tank's level, a site's last_refill_at, the reports and possibly a
      // request's status. Everything tenant-scoped is now suspect — invalidate the org.
      await queryClient.invalidateQueries({ queryKey: qk.org(orgId) });
    },
  });
}

/**
 * Amend a refill. The server holds the same line the UI does: a `completed` or `cancelled`
 * refill is a closed audit record and only its `notes` may change — anything else is a 409.
 */
export function useUpdateRefill() {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      refillId,
      ...body
    }: UpdateRefillBody & { refillId: string }): Promise<Refill> =>
      unwrap(api.PATCH("/v1/refills/{refillId}", { params: { path: { refillId } }, body })),
    onSuccess: async () => {
      // An amended refill moves the same things a new one does — the tank, the ledger,
      // the reports. Same blast radius, same invalidation.
      await queryClient.invalidateQueries({ queryKey: qk.org(orgId) });
    },
  });
}

export function useDeleteRefill() {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (refillId: string) =>
      unwrap(api.DELETE("/v1/refills/{refillId}", { params: { path: { refillId } } })),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.org(orgId) });
    },
  });
}

/* -------------------------------------------------------- refill requests */

export type RequestFilters = {
  site_id?: string;
  status?: "pending" | "approved" | "declined" | "fulfilled" | "cancelled";
  limit?: number;
};

export function useRefillRequests(filters: RequestFilters = {}) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: qk.refillRequests(orgId, filters),
    queryFn: () => unwrap(api.GET("/v1/refill-requests", { params: { query: filters } })),
  });
}

export function useCreateRefillRequest() {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      site_id: string;
      tank_id?: string;
      requested_liters: number;
      needed_by?: string;
      notes?: string;
    }): Promise<RefillRequest> =>
      unwrap(
        api.POST("/v1/refill-requests", {
          body,
          params: { header: { "Idempotency-Key": idempotencyKey() } },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [...qk.org(orgId), "refill-requests"] });
    },
  });
}

export function useApproveRequest() {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      requestId,
      ...body
    }: {
      requestId: string;
      approved_liters?: number;
      note?: string;
    }): Promise<RefillRequest> =>
      unwrap(
        api.POST("/v1/refill-requests/{requestId}/approve", {
          params: { path: { requestId } },
          body,
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [...qk.org(orgId), "refill-requests"] });
    },
  });
}

export function useDeclineRequest() {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      requestId,
      note,
    }: {
      requestId: string;
      note: string;
    }): Promise<RefillRequest> =>
      unwrap(
        api.POST("/v1/refill-requests/{requestId}/decline", {
          params: { path: { requestId } },
          body: { note },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [...qk.org(orgId), "refill-requests"] });
    },
  });
}

export function useCancelRequest() {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) =>
      unwrap(
        api.DELETE("/v1/refill-requests/{requestId}", { params: { path: { requestId } } }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [...qk.org(orgId), "refill-requests"] });
    },
  });
}

/* ---------------------------------------------------------------- reports */

export function useReportSummary(
  params: { from: string; to: string; site_id?: string; region_id?: string },
  options?: Partial<UseQueryOptions<ReportSummary>>,
) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: qk.reportSummary(orgId, params),
    queryFn: () => unwrap(api.GET("/v1/reports/summary", { params: { query: params } })),
    ...options,
  });
}

export function useReportExports() {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: qk.reportExports(orgId),
    queryFn: () => unwrap(api.GET("/v1/reports/exports", {})),
    // A pending export is rendered by a worker. Poll until nothing is pending any more.
    refetchInterval: (query) => {
      const data = query.state.data as { data?: ReportExport[] } | undefined;
      const pending = data?.data?.some((e) => e.status === "pending");
      return pending ? 3_000 : false;
    },
  });
}

export function useCreateReportExport() {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      report: ReportType;
      format: ReportFormat;
      from: string;
      to: string;
      site_id?: string;
      region_id?: string;
    }): Promise<ReportExport> => unwrap(api.POST("/v1/reports/exports", { body })),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.reportExports(orgId) });
    },
  });
}

export function reportDownloadUrl(exportId: string): string {
  const base = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8080";
  return `${base}/v1/reports/exports/${exportId}/download`;
}

/* ------------------------------------------------------ members & invites */

export function useMembers() {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: qk.members(orgId),
    queryFn: () =>
      unwrap(api.GET("/v1/organizations/{orgId}/members", { params: { path: { orgId } } })),
  });
}

export function useUpdateMemberRole() {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      role,
    }: {
      userId: string;
      role: OrganizationRole;
    }): Promise<Member> =>
      unwrap(
        api.PATCH("/v1/organizations/{orgId}/members/{userId}", {
          params: { path: { orgId, userId } },
          body: { role },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.members(orgId) });
      await queryClient.invalidateQueries({ queryKey: qk.me() });
    },
  });
}

export function useRemoveMember() {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      unwrap(
        api.DELETE("/v1/organizations/{orgId}/members/{userId}", {
          params: { path: { orgId, userId } },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.members(orgId) });
    },
  });
}

export function useInvitations() {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: qk.invitations(orgId),
    queryFn: () =>
      unwrap(api.GET("/v1/organizations/{orgId}/invitations", { params: { path: { orgId } } })),
  });
}

export function useCreateInvitation() {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; role: OrganizationRole }): Promise<Invitation> =>
      unwrap(
        api.POST("/v1/organizations/{orgId}/invitations", {
          params: { path: { orgId } },
          body,
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.invitations(orgId) });
    },
  });
}

export function useRevokeInvitation() {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      unwrap(
        api.DELETE("/v1/organizations/{orgId}/invitations/{invitationId}", {
          params: { path: { orgId, invitationId } },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.invitations(orgId) });
    },
  });
}

/** Public — an invitee is not signed in yet, so this is deliberately NOT org-scoped. */
export function useInvitationPreview(token: string) {
  return useQuery({
    queryKey: qk.invitationByToken(token),
    queryFn: (): Promise<InvitationPreview> =>
      unwrap(api.GET("/v1/invitations/{token}", { params: { path: { token } } })),
    retry: false,
  });
}

export function useAcceptInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (token: string): Promise<Session> => {
      const session = await unwrap(
        api.POST("/v1/invitations/{token}/accept", { params: { path: { token } } }),
      );
      if (session.access_token) tokenStore.set(session.access_token, session.expires_at);
      return session;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.me() });
      await queryClient.invalidateQueries({ queryKey: qk.myOrganizations() });
    },
  });
}

/* --------------------------------------------------------------- api keys */

export function useApiKeys() {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: qk.apiKeys(orgId),
    queryFn: () =>
      unwrap(api.GET("/v1/organizations/{orgId}/api-keys", { params: { path: { orgId } } })),
  });
}

export function useCreateApiKey() {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      scopes: ("ingest:write" | "data:read")[];
      expires_at?: string;
    }): Promise<ApiKeyCreated> =>
      unwrap(
        api.POST("/v1/organizations/{orgId}/api-keys", { params: { path: { orgId } }, body }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.apiKeys(orgId) });
    },
  });
}

export function useRevokeApiKey() {
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (keyId: string) =>
      unwrap(
        api.DELETE("/v1/organizations/{orgId}/api-keys/{keyId}", {
          params: { path: { orgId, keyId } },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.apiKeys(orgId) });
    },
  });
}
